import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Counter } from "../lib/motion";
import { Zap, Snowflake, Cpu } from "lucide-react";

const IMG = "https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000";

const STATS = [
  { icon: Cpu, label: "GPU cluster density", val: <><Counter to={40} suffix="k+" /></> },
  { icon: Zap, label: "Power delivered", val: <><Counter to={250} suffix="MW" /></> },
  { icon: Snowflake, label: "Liquid-cooled PUE", val: <><Counter to={1.08} decimals={2} /></> },
];

export default function DataCenter() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.25, 1]);
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section
      id="data-center"
      ref={ref}
      className="relative overflow-hidden border-t border-white/5"
      data-testid="datacenter-section"
    >
      <div className="absolute inset-0">
        <motion.img
          src={IMG}
          alt="Hyperscale data center"
          style={{ scale, y }}
          className="h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-nexis-bg via-nexis-bg/60 to-nexis-bg" />
        <div className="absolute inset-0 bg-gradient-to-r from-nexis-bg/90 via-transparent to-transparent" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 py-32 md:px-12 md:py-48">
        <Reveal>
          <span className="font-mono text-xs tracking-[0.25em] text-nexis-blue">— SCENE 03 / INFRASTRUCTURE</span>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 max-w-3xl font-display text-4xl font-light leading-tight tracking-tight lg:text-6xl">
            Walk through the <span className="text-gradient">machinery of the AI age</span>.
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-white/60">
            Servers illuminate. Fiber pulses. Cooling systems breathe. We modernize hyperscale and
            enterprise data centers into GPU-dense, liquid-cooled powerhouses ready for frontier compute.
          </p>
        </Reveal>

        <div className="mt-16 grid max-w-3xl gap-4 sm:grid-cols-3">
          {STATS.map((s, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="glass glass-hover rounded-2xl p-6" data-testid={`dc-stat-${i}`}>
                <s.icon className="h-5 w-5 text-nexis-blue" />
                <div className="mt-6 font-display text-4xl font-semibold tracking-tight">{s.val}</div>
                <div className="mt-2 text-xs uppercase tracking-wider text-white/45">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
